import { FC, useCallback, useEffect, useMemo } from 'react';
import { useApplicationStateLoader, useApplicationStateValue, useRouteContext } from '@resistdesign/voltra/app/utils';
import styled from 'styled-components';
import { AdminInfo } from '../../common/utils/AdminUtils';
import { Profile } from '../../common/Types';
import { Tabs, TabItem } from '../Components/Tabs';
import { PROFILE } from './Profile/IDs';
import { useProfileRPCs } from './Profile';
import { AdminMenu } from './Admin/AdminMenu';
import { CandidateSearch } from './Admin/CandidateSearch';
import { AdminTools } from './Admin/AdminTools';

const ADMIN_PATHS = {
  MENU: '/admin',
  SEARCH: '/admin/search',
  TOOLS: '/admin/tools',
} as const;

const AdminRoot = styled.div`
  height: 100%;
  min-height: 0;
`;
const RoutedAdminView = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;
const AdminNavigation = styled.nav`
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
  margin-bottom: 1em;
`;
const AdminNavigationTabs = styled(Tabs).attrs({ className: 'admin-view-tabs' })`
  &.admin-view-tabs {
    width: unset;

    & > button {
      padding: 0.125em 1em;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
`;
const RoutedAdminContent = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
`;
const AdminToolsContent = styled(RoutedAdminContent)`
  overflow: auto;
`;

export type AdminProps = {
  authorization: string;
  adminInfo: AdminInfo;
};

export const Admin: FC<AdminProps> = ({ authorization, adminInfo }) => {
  const { isSystemAdmin, isGroupAdmin } = adminInfo;
  const { currentWindowPath = '' } = useRouteContext();
  const { profileRPC } = useProfileRPCs({ authorization });
  const { loading: adminProfileLoading } = useApplicationStateLoader({
    identifier: PROFILE.READ,
    remoteProcedureCall: profileRPC,
  });
  const { value: rawAdminProfile } = useApplicationStateValue(PROFILE.READ);
  const adminProfile = useMemo<Partial<Profile>>(() => rawAdminProfile ?? {}, [rawAdminProfile]);
  const { groupAdminIdCode } = adminProfile;

  const onSelectMenu = useCallback(() => {
    history.pushState({}, '', ADMIN_PATHS.MENU);
  }, []);
  const onSelectSearch = useCallback(() => {
    history.pushState({}, '', ADMIN_PATHS.SEARCH);
  }, []);
  const onSelectTools = useCallback(() => {
    history.pushState({}, '', ADMIN_PATHS.TOOLS);
  }, []);
  const adminTabItems = useMemo<TabItem[]>(
    () => [
      { label: 'Menu', onClick: onSelectMenu },
      { label: 'Search', onClick: onSelectSearch },
      { label: 'Tools', onClick: onSelectTools },
    ],
    [onSelectMenu, onSelectSearch, onSelectTools]
  );

  useEffect(() => {
    if (
      isGroupAdmin &&
      currentWindowPath !== ADMIN_PATHS.MENU &&
      currentWindowPath !== ADMIN_PATHS.SEARCH &&
      currentWindowPath !== ADMIN_PATHS.TOOLS
    ) {
      history.pushState({}, '', ADMIN_PATHS.MENU);
    }
  }, [currentWindowPath, isGroupAdmin]);

  if (!isGroupAdmin) {
    return (
      <CandidateSearch
        authorization={authorization}
        isSystemAdmin={isSystemAdmin}
        isGroupAdmin={false}
        footer={
          adminProfileLoading ? (
            <article aria-busy="true"></article>
          ) : (
            <AdminTools
              authorization={authorization}
              groupAdminIdCode={groupAdminIdCode}
              adminIsSystemAdmin={isSystemAdmin}
              adminIsGroupAdmin={false}
            />
          )
        }
      />
    );
  }

  if (currentWindowPath === ADMIN_PATHS.MENU) {
    return (
      <AdminRoot>
        <AdminMenu onSelectSearch={onSelectSearch} onSelectTools={onSelectTools} />
      </AdminRoot>
    );
  }

  const selectedAdminTabIndex = currentWindowPath === ADMIN_PATHS.SEARCH ? 1 : 2;

  return (
    <RoutedAdminView>
      <AdminNavigation aria-label="Employer admin navigation">
        <AdminNavigationTabs tabItems={adminTabItems} selectedIndex={selectedAdminTabIndex} />
      </AdminNavigation>
      {currentWindowPath === ADMIN_PATHS.SEARCH ? (
        <RoutedAdminContent>
          <CandidateSearch authorization={authorization} isSystemAdmin={isSystemAdmin} isGroupAdmin />
        </RoutedAdminContent>
      ) : undefined}
      {currentWindowPath === ADMIN_PATHS.TOOLS ? (
        <AdminToolsContent>
          {adminProfileLoading ? (
            <article aria-busy="true"></article>
          ) : (
            <AdminTools
              authorization={authorization}
              groupAdminIdCode={groupAdminIdCode}
              adminIsSystemAdmin={isSystemAdmin}
              adminIsGroupAdmin
            />
          )}
        </AdminToolsContent>
      ) : undefined}
    </RoutedAdminView>
  );
};
